import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Seo from "./Seo";
import useLangPrefix from "../hooks/useLangPrefix";
import useRevealSection from "../hooks/useRevealSection";

interface IncomeClockProps {
  lang: "de" | "en";
}

interface Feature {
  title: string;
  text: string;
}

/** Store buttons. Set `url` once an app is live to turn the tile into a link. */
const STORES: { key: "appstore" | "playstore" | "macappstore"; icon: string; url: string | null }[] = [
  { key: "appstore", icon: "bxl-apple", url: null },
  { key: "playstore", icon: "bxl-play-store", url: null },
  { key: "macappstore", icon: "bxl-apple", url: null },
];

const SHOTS = ["01", "02", "03", "04"] as const;

const IncomeClock: React.FC<IncomeClockProps> = ({ lang }) => {
  const { t } = useTranslation();
  const prefix = useLangPrefix();
  const sectionRef = useRevealSection<HTMLElement>();

  const features = t("incomeClock.features.items", {
    returnObjects: true,
  }) as Feature[];
  const platforms = t("incomeClock.platforms.items", {
    returnObjects: true,
  }) as string[];

  // Screenshot lightbox: index of the shot shown enlarged, or null when closed.
  const [lightbox, setLightbox] = useState<number | null>(null);

  const close = useCallback(() => setLightbox(null), []);
  const step = useCallback(
    (dir: number) =>
      setLightbox((i) =>
        i === null ? i : (i + dir + SHOTS.length) % SHOTS.length
      ),
    []
  );

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowLeft") step(-1);
      else if (e.key === "ArrowRight") step(1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightbox, close, step]);

  return (
    <section className="ic" ref={sectionRef}>
      <Seo
        lang={lang}
        path="/projekte/income-clock"
        title={t("incomeClock.seoTitle")}
        description={t("incomeClock.seoDescription")}
        keywords={t("incomeClock.seoKeywords") as string}
      />

      <a href={`${prefix}/#projects`} className="ic-back">
        <i className="bx bx-arrow-back"></i> {t("incomeClock.back")}
      </a>

      <div className="ic-hero reveal">
        <img
          src="/images/income-clock/icon.png"
          alt={lang === "de" ? "Income Clock App-Icon" : "Income Clock app icon"}
          className="ic-icon"
          width={128}
          height={128}
        />
        <div className="ic-hero-text">
          <h1>Income Clock</h1>
          <p className="ic-badge">{t("incomeClock.badge")}</p>
          <p className="ic-pitch">{t("incomeClock.pitch")}</p>
        </div>
      </div>

      <p className="ic-intro reveal">{t("incomeClock.intro")}</p>

      <div className="ic-block reveal">
        <h2>{t("incomeClock.stores.heading")}</h2>
        <div className="ic-stores">
          {STORES.map(({ key, icon, url }) =>
            url ? (
              <a
                key={key}
                className="ic-store-btn"
                href={url}
                target="_blank"
                rel="noreferrer"
              >
                <i className={`bx ${icon}`}></i>
                {t(`incomeClock.stores.${key}`)}
              </a>
            ) : (
              <span key={key} className="ic-store-btn is-soon">
                <i className={`bx ${icon}`}></i>
                {t(`incomeClock.stores.${key}`)}
                <em>{t("incomeClock.stores.soon")}</em>
              </span>
            )
          )}
        </div>
        <p className="ic-note">{t("incomeClock.stores.note")}</p>
      </div>

      <div className="ic-block reveal">
        <h2>{t("incomeClock.features.heading")}</h2>
        <ul className="ic-features">
          {features.map((f) => (
            <li key={f.title}>
              <strong>{f.title}</strong>
              <span>{f.text}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="ic-block reveal">
        <h2>{t("incomeClock.shots.heading")}</h2>
        <p className="ic-note">{t("incomeClock.shots.hint")}</p>
        <div className="ic-shots stagger">
          {SHOTS.map((n, i) => (
            <figure key={n} className="reveal">
              <button
                type="button"
                className="ic-shot-btn"
                onClick={() => setLightbox(i)}
                aria-label={t(`incomeClock.shots.${n}`)}
              >
                <img
                  src={`/images/income-clock/iphone/${lang}/${n}.jpg`}
                  alt={t(`incomeClock.shots.${n}`)}
                  loading="lazy"
                />
              </button>
              <figcaption>{t(`incomeClock.shots.${n}`)}</figcaption>
            </figure>
          ))}
        </div>
      </div>

      <div className="ic-block reveal">
        <h2>{t("incomeClock.platforms.heading")}</h2>
        <ul className="ic-list">
          {platforms.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      </div>

      <div className="ic-block reveal">
        <h2>{t("incomeClock.privacy.teaserHeading")}</h2>
        <p>{t("incomeClock.privacy.teaser")}</p>
        <a
          href={`${prefix}/projekte/income-clock/datenschutz`}
          className="ic-store-btn"
        >
          <i className="bx bx-lock-alt"></i>
          {t("incomeClock.privacy.linkLabel")}
        </a>
      </div>

      <div className="ic-block reveal">
        <h2>{t("incomeClock.contact.heading")}</h2>
        <p>{t("incomeClock.contact.developer")}</p>
        <p>{t("incomeClock.contact.address")}</p>
        <p>
          {t("incomeClock.contact.emailLabel")}{" "}
          <a href={`mailto:${t("incomeClock.contact.email")}`}>
            {t("incomeClock.contact.email")}
          </a>
        </p>
        <p>
          {t("incomeClock.contact.formPre")}
          <a href={`${prefix}/contact`}>{t("incomeClock.contact.formLink")}</a>
          {t("incomeClock.contact.formMid")}
          <a href="/impressum">{t("incomeClock.contact.imprintLink")}</a>
          {t("incomeClock.contact.formPost")}
        </p>
      </div>

      {lightbox !== null && (
        <div
          className="ic-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={t(`incomeClock.shots.${SHOTS[lightbox]}`)}
          onClick={(e) => {
            if (e.target === e.currentTarget) close();
          }}
        >
          <button
            type="button"
            className="ic-lightbox-close"
            onClick={close}
            aria-label={lang === "de" ? "Schließen" : "Close"}
          >
            <i className="bx bx-x"></i>
          </button>
          <button
            type="button"
            className="ic-lightbox-nav prev"
            onClick={() => step(-1)}
            aria-label={lang === "de" ? "Vorheriges Bild" : "Previous image"}
          >
            <i className="bx bx-chevron-left"></i>
          </button>
          <img
            src={`/images/income-clock/iphone/${lang}/${SHOTS[lightbox]}.jpg`}
            alt={t(`incomeClock.shots.${SHOTS[lightbox]}`)}
          />
          <button
            type="button"
            className="ic-lightbox-nav next"
            onClick={() => step(1)}
            aria-label={lang === "de" ? "Nächstes Bild" : "Next image"}
          >
            <i className="bx bx-chevron-right"></i>
          </button>
          <p className="ic-lightbox-caption">
            {t(`incomeClock.shots.${SHOTS[lightbox]}`)} · {lightbox + 1}/
            {SHOTS.length}
          </p>
        </div>
      )}
    </section>
  );
};

export default IncomeClock;
